import { useState, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../hooks/useAuth";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";
import type { Connector, BackupFile } from "./Dashboard";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8787";

interface BackupJob {
  id:            string;
  connector_id:  string;
  triggered_by:  string;
  status:        string;
  data_types:    string[];
  total_records: Record<string, number> | null;
  total_bytes:   number | null;
  completed_at:  string | null;
  queued_at:     string;
  files?:        BackupFile[];
}

// ── Labels for all 27 data types ────────────────────────────────────────────
const DATA_TYPE_LABELS: Record<string, string> = {
  invoices:                     "Invoices",
  invoice_pdfs:                 "Invoice PDFs",
  credit_notes:                 "Credit notes",
  bills:                        "Bills",
  contacts:                     "Contacts",
  bank_transactions:            "Bank transactions",
  expenses:                     "Expenses",
  attachments:                  "Attachments",
  vat_returns:                  "VAT returns",
  payroll:                      "Payroll",
  estimates:                    "Estimates",
  projects:                     "Projects",
  timeslips:                    "Timeslips",
  tasks:                        "Tasks",
  users:                        "Users",
  capital_assets:               "Capital assets",
  corporation_tax_returns:      "Corporation tax returns",
  final_accounts_reports:       "Final accounts reports",
  journal_entries:              "Journal entries",
  price_list_items:             "Price list items",
  sender_email_addresses:       "Sender email addresses",
  company:                      "Company",
  hire_purchases:               "Hire purchases",
  income_tax_returns:           "Income tax returns",
  year_ends:                    "Year ends",
};

// Types that are stored as many individual S3 objects rather than one JSON file
const INDEX_TYPES = new Set(["attachments", "invoice_pdfs"]);

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatBytes(bytes: number | null): string {
  if (!bytes) return "—";
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1_024)     return `${(bytes / 1_024).toFixed(1)} KB`;
  return `${bytes} B`;
}
function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
function formatDateTime(ts: string): string {
  return `${formatDate(ts)} at ${formatTime(ts)}`;
}
function totalRecordCount(records: Record<string, number> | null): number {
  if (!records) return 0;
  return Object.values(records).reduce((a, b) => a + b, 0);
}
function connectorLabel(connectors: Connector[], id: string): string {
  const c = connectors.find(c => c.id === id);
  if (!c) return "Unknown connector";
  const type = c.connector_type ?? "";
  const name = type === "freeagent" ? "FreeAgent" : type === "xero" ? "Xero" : type;
  return c.display_name ? `${name} — ${c.display_name}` : name;
}

// ── Main component ─────────────────────────────────────────────────────────
export default function RestorePage() {
  const { get }         = useApi();
  const { token }       = useAuth();

  const [jobs, setJobs]             = useState<BackupJob[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading]       = useState(true);
  const [total, setTotal]           = useState(0);
  const [offset, setOffset]         = useState(0);
  const [connectorFilter, setConnectorFilter] = useState("");
  const [expanded, setExpanded]     = useState<string | null>(null);
  const [jobFiles, setJobFiles]     = useState<Record<string, BackupFile[]>>({});
  const [loadingFiles, setLoadingFiles] = useState<string | null>(null);
  const [downloading, setDownloading]  = useState<string | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const limit = 20;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ status: "complete", limit: String(limit), offset: String(offset) });
    Promise.all([
      get<{ jobs: BackupJob[]; total: number }>(`/api/jobs?${params}`),
      get<{ connectors: Connector[] }>("/api/connectors"),
    ]).then(([jobsRes, connRes]) => {
      setJobs(jobsRes.jobs ?? []);
      setTotal(jobsRes.total ?? 0);
      setConnectors(connRes.connectors ?? []);
    }).catch(err => setError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [connectorFilter]);

  // Load files for a job when expanded
  const handleExpand = useCallback(async (jobId: string) => {
    if (expanded === jobId) { setExpanded(null); return; }
    setExpanded(jobId);
    if (jobFiles[jobId]) return; // already loaded
    setLoadingFiles(jobId);
    try {
      const res = await get<{ job: BackupJob }>(`/api/jobs/${jobId}`);
      setJobFiles(prev => ({ ...prev, [jobId]: res.job.files ?? [] }));
    } catch { /* silent */ } finally {
      setLoadingFiles(null);
    }
  }, [expanded, jobFiles, get]);

  // Proxy download via CF Worker — response IS the file (blob)
  const handleDownload = useCallback(async (storageKey: string, filename: string) => {
    if (downloading) return;
    setDownloading(storageKey);
    try {
      const res = await fetch(
        `${API_BASE}/api/restore/download?key=${encodeURIComponent(storageKey)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) { alert("Download failed — please try again."); return; }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } finally {
      setDownloading(null);
    }
  }, [token, downloading]);

  const filtered = connectorFilter
    ? jobs.filter(j => j.connector_id === connectorFilter)
    : jobs;

  const grouped = filtered.reduce<Record<string, BackupJob[]>>((acc, job) => {
    const date = formatDate(job.completed_at ?? job.queued_at);
    if (!acc[date]) acc[date] = [];
    acc[date].push(job);
    return acc;
  }, {});

  const uniqueConnectors = connectors.filter(c => jobs.some(j => j.connector_id === c.id));

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Restore"
          subtitle={total ? `${total} completed backup${total !== 1 ? "s" : ""}` : "Browse your backed up data"}
          action={
            uniqueConnectors.length > 1 ? (
              <select
                className="form-input"
                value={connectorFilter}
                onChange={e => setConnectorFilter(e.target.value)}
                style={{ width: 200, padding: "6px 10px", fontSize: 13 }}
              >
                <option value="">All connectors</option>
                {uniqueConnectors.map(c => (
                  <option key={c.id} value={c.id}>{connectorLabel(connectors, c.id)}</option>
                ))}
              </select>
            ) : undefined
          }
        />
        <main className="page">
          {error && <PageError message={error} onRetry={load} />}

          {!error && (loading ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>⬡</div>
              <p style={{ fontWeight: 500, color: "var(--text2)" }}>No completed backups yet</p>
              <p style={{ fontSize: 13 }}>Run a backup from the Overview page to get started.</p>
            </div>
          ) : (
            <>
              {Object.entries(grouped).map(([date, dayJobs]) => (
                <div key={date} style={{ marginBottom: 28 }}>
                  <div style={{
                    fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)",
                    letterSpacing: "0.07em", textTransform: "uppercase",
                    padding: "0 0 10px 0", borderBottom: "1px solid var(--border)",
                    marginBottom: 8,
                  }}>{date}</div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {dayJobs.map(job => {
                      const isOpen    = expanded === job.id;
                      const records   = job.total_records ?? {};
                      const totalRecs = totalRecordCount(job.total_records);
                      const connector = connectorLabel(connectors, job.connector_id);
                      const files     = jobFiles[job.id] ?? [];

                      return (
                        <div
                          key={job.id}
                          className="card"
                          style={{ padding: 0, overflow: "hidden" }}
                        >
                          {/* ── Summary row (clickable) ── */}
                          <div
                            style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" }}
                            onClick={() => handleExpand(job.id)}
                          >
                            <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", flexShrink: 0, width: 44 }}>
                              {formatTime(job.completed_at ?? job.queued_at)}
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>{connector}</div>
                              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                                {totalRecs.toLocaleString()} records · {formatBytes(job.total_bytes)}
                                {job.triggered_by === "manual" && (
                                  <span style={{ marginLeft: 6, fontFamily: "var(--mono)", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: "var(--r)", padding: "0 4px" }}>manual</span>
                                )}
                              </div>
                            </div>

                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                              {Object.keys(records).slice(0, 6).map(dt => (
                                <span key={dt} style={{
                                  fontSize: 10, fontFamily: "var(--mono)",
                                  background: "var(--surface2)", border: "1px solid var(--border)",
                                  borderRadius: "var(--r)", padding: "1px 6px", color: "var(--text3)",
                                }}>
                                  {DATA_TYPE_LABELS[dt] ?? dt}
                                </span>
                              ))}
                              {Object.keys(records).length > 6 && (
                                <span style={{ fontSize: 10, fontFamily: "var(--mono)", color: "var(--text3)", padding: "1px 4px" }}>
                                  +{Object.keys(records).length - 6} more
                                </span>
                              )}
                            </div>

                            <div style={{
                              color: "var(--text3)", fontSize: 10, flexShrink: 0,
                              transform: isOpen ? "rotate(180deg)" : "none",
                              transition: "transform 0.15s",
                            }}>▼</div>
                          </div>

                          {/* ── Expanded: file download table ── */}
                          {isOpen && (
                            <div
                              style={{ borderTop: "1px solid var(--border)", background: "var(--surface2)" }}
                              onClick={e => e.stopPropagation()}
                            >
                              {loadingFiles === job.id ? (
                                <div style={{ padding: "16px", fontSize: 12, color: "var(--text3)" }}>Loading files…</div>
                              ) : (
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                  <thead>
                                    <tr>
                                      {["Data type", "Records", "Size", ""].map(h => (
                                        <th key={h} style={{
                                          textAlign: "left", padding: "8px 16px",
                                          fontSize: 10, fontFamily: "var(--mono)", color: "var(--text3)",
                                          fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.06em",
                                          borderBottom: "1px solid var(--border)",
                                        }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {files.length === 0 ? (
                                      // Fall back to total_records if files aren't in DB yet
                                      Object.entries(records).map(([dt, count]) => (
                                        <tr key={dt} style={{ borderBottom: "1px solid var(--border)" }}>
                                          <td style={{ padding: "8px 16px", color: "var(--text2)", fontWeight: 500 }}>
                                            {DATA_TYPE_LABELS[dt] ?? dt}
                                          </td>
                                          <td style={{ padding: "8px 16px", fontFamily: "var(--mono)", fontSize: 12 }}>
                                            {count.toLocaleString()}
                                          </td>
                                          <td style={{ padding: "8px 16px", fontFamily: "var(--mono)", fontSize: 12, color: "var(--text3)" }}>
                                            —
                                          </td>
                                          <td style={{ padding: "8px 16px", textAlign: "right" }}>
                                            <span style={{ fontSize: 11, color: "var(--text3)" }}>no file record</span>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      files.map(file => {
                                        const isIndex = INDEX_TYPES.has(file.data_type);
                                        const isDownloading = downloading === file.storage_key;
                                        return (
                                          <tr key={file.id} style={{ borderBottom: "1px solid var(--border)" }}>
                                            <td style={{ padding: "8px 16px", color: "var(--text2)", fontWeight: 500 }}>
                                              {DATA_TYPE_LABELS[file.data_type] ?? file.data_type}
                                              {isIndex && (
                                                <span style={{ marginLeft: 6, fontSize: 10, fontFamily: "var(--mono)", color: "var(--text3)" }}>index</span>
                                              )}
                                            </td>
                                            <td style={{ padding: "8px 16px", fontFamily: "var(--mono)", fontSize: 12 }}>
                                              {file.record_count.toLocaleString()}
                                            </td>
                                            <td style={{ padding: "8px 16px", fontFamily: "var(--mono)", fontSize: 12, color: "var(--text3)" }}>
                                              {formatBytes(file.size_bytes)}
                                            </td>
                                            <td style={{ padding: "8px 16px", textAlign: "right" }}>
                                              <button
                                                className="btn btn-ghost btn-sm"
                                                disabled={isDownloading || !!downloading}
                                                onClick={() => handleDownload(file.storage_key, file.storage_key.split("/").pop() ?? "download")}
                                                style={{ fontSize: 11 }}
                                              >
                                                {isDownloading ? "Getting link…" : "↓ Download"}
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })
                                    )}
                                  </tbody>
                                  <tfoot>
                                    <tr>
                                      <td style={{ padding: "8px 16px", fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                                        Job ID: {job.id.slice(0, 8)}…
                                      </td>
                                      <td style={{ padding: "8px 16px", fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                                        {totalRecs.toLocaleString()} total
                                      </td>
                                      <td style={{ padding: "8px 16px", fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                                        {formatBytes(job.total_bytes)}
                                      </td>
                                      <td style={{ padding: "8px 16px", textAlign: "right", fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                                        {formatDateTime(job.completed_at ?? job.queued_at)}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Pagination */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, fontSize: 13, color: "var(--text3)" }}>
                <span>{offset + 1}–{Math.min(offset + limit, offset + filtered.length)} of {total}</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - limit))}>← Prev</button>
                  <button className="btn btn-ghost btn-sm" disabled={filtered.length < limit} onClick={() => setOffset(o => o + limit)}>Next →</button>
                </div>
              </div>
            </>
          ))}
        </main>
      </div>
    </div>
  );
}
